import { MenuItem, TextField } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "../../lib/supabase";
import { useAuthStore } from "../../stores/auth";

// Shared "Assigned to" picker for activities. Renders the assignee's name
// (full_name, falling back to the login email) — never a raw email — and is
// reused by the timeline composer/edit, the Activities dialog, and the detail
// pane so the display stays consistent everywhere an owner is chosen.

export interface AssignableUser {
  id: string;
  email: string;
  full_name: string | null;
  avatar_url: string | null;
  is_active: boolean;
}

/** Display label for an assignee: their name, or the email if unnamed. */
export const assigneeLabel = (u: Pick<AssignableUser, "email" | "full_name">) =>
  u.full_name || u.email;

export function useAssignableUsers() {
  return useQuery({
    queryKey: ["assignable_users"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("manna_list_assignable_users");
      if (error) throw error;
      return (data ?? []) as AssignableUser[];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function AssigneePicker({
  value,
  onChange,
  label = "Assigned to",
  size = "small",
  fullWidth = true,
  disabled = false,
}: {
  value: string | null;
  onChange: (id: string | null) => void;
  label?: string;
  size?: "small" | "medium";
  fullWidth?: boolean;
  disabled?: boolean;
}) {
  const currentUserId = useAuthStore((s) => s.user?.id ?? null);
  const { data, isLoading } = useAssignableUsers();
  const users = data ?? [];

  const nameOf = (id: string) => {
    const u = users.find((x) => x.id === id);
    if (u) return assigneeLabel(u);
    return id === currentUserId ? "You" : id;
  };

  return (
    <TextField
      select
      size={size}
      label={label}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value || null)}
      fullWidth={fullWidth}
      disabled={disabled || isLoading}
      SelectProps={{ renderValue: (val) => nameOf(val as string) }}
    >
      {users.map((u) => (
        <MenuItem key={u.id} value={u.id}>
          {assigneeLabel(u)}
          {u.id === currentUserId ? " (you)" : ""}
        </MenuItem>
      ))}
      {/* Keep the current user selectable while the list is still loading. */}
      {!data && currentUserId && <MenuItem value={currentUserId}>You</MenuItem>}
    </TextField>
  );
}
